import { useCallback, useEffect, useState } from "react";
import { BellRing, HandCoins, Loader2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Empty, Err, Ok, Panel } from "@/components/community/kit";
import { day } from "@/lib/community";

const Metric = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-ink-600 bg-ink-900/60 p-4">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-1.5 font-display text-xl text-slate-50">{value}</p>
  </div>
);

export const DuesLedger = ({ community, onChanged }) => {
  const cid = community.id;
  const [data, setData] = useState(null);
  const [entry, setEntry] = useState({ user_id: "", kind: "monthly", amount: 0, method: "cash", months: 1, note: "" });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/dues/ledger`);
      setData(res);
      setEntry((e) => ({ ...e, amount: e.amount || res.dues.monthly }));
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const record = async () => {
    setBusy("record"); setError(""); setNotice("");
    try {
      await api.post(`/communities/${cid}/dues/record`, entry);
      setNotice("Recorded — that member's dues are up to date.");
      setEntry({ user_id: "", kind: "monthly", amount: data.dues.monthly, method: "cash", months: 1, note: "" });
      await load();
      onChanged?.();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const remind = async () => {
    setBusy("remind"); setError(""); setNotice("");
    try {
      const { data: res } = await api.post(`/communities/${cid}/dues/remind`);
      setNotice(`Reminder sent to ${res.reminded} member${res.reminded === 1 ? "" : "s"}.`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <div className="space-y-4">
      <Panel testId="dues-ledger" title="The books">
        <Err testId="ledger-error">{error}</Err>
        <Ok testId="ledger-notice">{notice}</Ok>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Metric testId="ledger-collected" label="Collected all time" value={money(data.totals.collected)} />
          <Metric testId="ledger-year" label={`Collected ${new Date().getFullYear()}`} value={money(data.totals.this_year)} />
          <Metric testId="ledger-paidup" label="Paid up" value={`${data.totals.paid_up}/${data.totals.members}`} />
          <Metric testId="ledger-behind" label="Behind" value={data.behind.length} />
        </div>

        {data.payments.length === 0 ? (
          <div className="mt-4"><Empty testId="ledger-empty">No dues collected yet.</Empty></div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead>
                <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="py-2">Member</th><th className="py-2">What</th>
                  <th className="py-2">How</th><th className="py-2">Covers to</th>
                  <th className="py-2 text-right">Amount</th><th className="py-2 text-right">Date</th>
                </tr>
              </thead>
              <tbody data-testid="ledger-table">
                {data.payments.map((p) => (
                  <tr key={p.id} data-testid={`ledger-row-${p.id}`} className="border-b border-ink-700/60">
                    <td className="py-2 text-slate-200">{p.name || "Member"}</td>
                    <td className="py-2 text-slate-400">{p.kind_label}</td>
                    <td className="py-2 text-slate-400">{p.method_label}</td>
                    <td className="py-2 text-slate-400">{p.covers_to ? day(p.covers_to) : "—"}</td>
                    <td className="py-2 text-right font-mono text-slate-200">{money(p.amount)}</td>
                    <td className="py-2 text-right font-mono text-xs text-slate-500">{day(p.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {data.behind.length > 0 && (
        <Panel testId="dues-behind" title={`Behind on dues · ${data.behind.length}`}
          action={
            <Button data-testid="dues-remind" size="sm" variant="outline" onClick={remind}
              disabled={busy === "remind"}>
              {busy === "remind" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <BellRing className="mr-2 h-4 w-4" />}
              Chase everyone
            </Button>
          }>
          <ul className="divide-y divide-ink-600">
            {data.behind.map((m) => (
              <li key={m.user_id} data-testid={`behind-row-${m.user_id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                <p className="truncate text-sm text-slate-200">{m.name}</p>
                <div className="flex items-center gap-2">
                  {m.in_grace
                    ? <Chip className="border-amber-500/40 bg-amber-500/10 text-amber-300">in grace</Chip>
                    : <Chip className="border-red-500/40 bg-red-500/10 text-red-300">{m.days_late} days late</Chip>}
                  <Button data-testid={`behind-record-${m.user_id}`} size="sm" variant="ghost"
                    onClick={() => setEntry({ ...entry, user_id: m.user_id, amount: data.dues.monthly })}>
                    Record a payment
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel testId="dues-record" title="Took cash at the meeting?"
        sub="Recording it here counts exactly the same as a card payment.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <select data-testid="record-member" value={entry.user_id}
            onChange={(e) => setEntry({ ...entry, user_id: e.target.value })}
            className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
            <option value="">Which member…</option>
            {data.roster.map((m) => (
              <option key={m.user_id} value={m.user_id}>{m.name}</option>
            ))}
          </select>
          <select data-testid="record-kind" value={entry.kind}
            onChange={(e) => setEntry({
              ...entry, kind: e.target.value,
              amount: e.target.value === "annual" ? data.dues.annual
                : e.target.value === "joining" ? data.dues.joining_fee : data.dues.monthly,
              months: e.target.value === "annual" ? 12 : 1,
            })}
            className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
            <option value="monthly">Monthly dues</option>
            <option value="annual">Annual dues</option>
            <option value="joining">Joining fee</option>
          </select>
          <select data-testid="record-method" value={entry.method}
            onChange={(e) => setEntry({ ...entry, method: e.target.value })}
            className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
            <option value="cash">Cash</option><option value="zelle">Zelle</option>
            <option value="check">Check</option><option value="transfer">Bank transfer</option>
            <option value="other">Other</option>
          </select>
          <Input data-testid="record-amount" type="number" min="0" step="0.01" placeholder="Amount"
            value={entry.amount} onChange={(e) => setEntry({ ...entry, amount: Number(e.target.value) })} />
          <Input data-testid="record-months" type="number" min="0" max="36" placeholder="Months covered"
            value={entry.months} onChange={(e) => setEntry({ ...entry, months: Number(e.target.value) })} />
          <Input data-testid="record-note" placeholder="Note (optional)" value={entry.note}
            onChange={(e) => setEntry({ ...entry, note: e.target.value })} />
        </div>
        <Button data-testid="record-save" className="mt-3" onClick={record}
          disabled={!entry.user_id || busy === "record"}>
          {busy === "record" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <HandCoins className="mr-2 h-4 w-4" />}
          Record it
        </Button>
      </Panel>
    </div>
  );
};
